import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";
import { SceneCanvas } from "./components/SceneCanvas/SceneCanvas";
import { GlbSceneCanvas } from "./components/GlbSceneCanvas/GlbSceneCanvas";
import { GlbDevPanel } from "./components/GlbDevPanel/GlbDevPanel";
import { SceneBackgroundSwitcher } from "./components/SceneBackgroundSwitcher/SceneBackgroundSwitcher";
import type { GlbSceneApi } from "./scene/glb/createGlbScene";
import type { SplatSceneApi } from "./scene/createScene";
import type { GlbSceneConfig } from "./scene/glb/glbSceneConfig";
import { getSplatSceneConfig, isSplatScene } from "./scene/splatRegistry";
import { getGlbSceneConfig, isGlbScene } from "./scene/glb/registry";
import { getPoiArtifactConfig } from "./scene/poiArtifacts/registry";
import {
  isInsideScene,
  getInsideSceneConfig,
  INSIDE_SCENE_PARENT,
} from "./scene/insideScenes/registry";
import { enterInsideScene } from "./state/insideSceneEntry";
import styles from "./App.module.css";
import { Leva } from "leva";
import { AppConfigPanel } from "./components/AppConfigPanel/AppConfigPanel";
import { SplatTransformPanel } from "./components/SplatTransformPanel/SplatTransformPanel";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { CameraViewOffsetProvider, HUD_REVEAL_DELAY } from "./context/CameraViewOffsetContext";
import { useLayoutMode } from "./hooks/useLayoutMode";
import { $appState, $sceneOrderedSightIds, $sceneSights, stopVirtualTour } from "./state/appState";
import { useUnit } from "effector-react";
import { AssistantHud } from "./components/AssistantHud";
import { OverallSceneOverlay } from "./components/OverallSceneOverlay";
import { $sceneLoadingState } from "./state/sceneLoading";
import { assistantSkipAllAnimations } from "./state/assistantHud";
import { SplashScreen } from "./components/SplashScreen/SplashScreen";
import { ParticleSpherePreloader } from "./components/ParticleSpherePreloader/ParticleSpherePreloader";
import { SceneLoadError } from "./components/SceneLoadError/SceneLoadError";
import { MenuOverlay } from "./components/MenuOverlay";
import { menuOpened } from "./state/sceneProgress";
import { $audioConfig, updateAudioConfig } from "./state/appConfig";
import { $configFetched } from "./state/locale";
import { Button } from "./components/Button";
import ListIcon from "./icons/list.svg?react";
import CaretLeftIcon from "./icons/caret_left.svg?react";
import CloseIcon from "./icons/close.svg?react";
import ClockIcon from "./icons/clock.svg?react";
import SunIcon from "./icons/sun.svg?react";
import MoonIcon from "./icons/moon.svg?react";
import { useTheme } from "./theme/useTheme";
import VolumeOnIcon from "./icons/volume_on.svg?react";
import VolumeOffIcon from "./icons/volume_off.svg?react";
import { useI18n, formatMessage } from "./i18n";
import type { Sight } from "./types";
import { SceneProgressBar } from "./components/SceneProgressBar/SceneProgressBar";
import { SceneOnboarding } from "./components/SceneOnboarding/SceneOnboarding";
import { SceneTitle } from "./components/SceneTitle/SceneTitle";
import { CameraDebugOverlay } from "./components/CameraDebugOverlay/CameraDebugOverlay";
import { RestrictedCameraTuner } from "./components/RestrictedCameraTuner/RestrictedCameraTuner";
import { NavPoiTuner } from "./components/NavPoiTuner/NavPoiTuner";
import { FlyoverTuner } from "./components/FlyoverTuner/FlyoverTuner";
import { CrowTuner } from "./components/CrowTuner/CrowTuner";
import { SightCameraConfigPanel } from "./components/SightCameraConfigPanel";
import { sceneEntered, sceneExited } from "./analytics/sceneTracker";
import { insideSceneBackPressed } from "./analytics/navigationTracker";
import { menuClosed } from "./analytics/menuTracker";
import { identifyLayoutMode, layoutModeChanged } from "./analytics/deviceTracker";
import { recordPoiArtifactOpen, recordPoiArtifactClose, logEvent as logMemoryEvent } from "./debug/memoryTracker";
import { isPoiArtifactCached } from "./scene/glb/poiArtifactHost";
import { $sceneStateSnapshots } from "./state/sceneState";
import { SceneExpandedEffect } from "./components/SceneExpandedEffect/SceneExpandedEffect";
import {
  $activeScene,
  $activeSceneId,
  $poiArtifactId,
  $isPoiArtifactOpen,
  $sceneStack,
  enterScene,
  goBack,
  openPoiArtifact,
  closePoiArtifact,
  resetNavigation,
  rebuildStack,
  type SceneStackEntry,
} from "./state/sceneNavigation";
import { KNOWN_SCENES, SCENE_POI_ID } from "./scene/sceneNavigationMap";
import { isEraScene, ERA_SCENE_PARENT } from "./scene/eraScenes/registry";
import {
  $historyMode,
  closeHistory,
  clockButtonTapped,
  sceneHasHistory,
  navigateToHistoricalScene,
} from "./state/historicalScenes";
import { HistoricalScenesSlider } from "./components/HistoricalScenesSlider";

/** Default root scene rendered when no other scene is active. */
const ROOT_SCENE_ID = "overall";

const rootStackEntry = (): SceneStackEntry => ({
  sceneId: ROOT_SCENE_ID,
  poiFilterId: SCENE_POI_ID[ROOT_SCENE_ID] ?? ROOT_SCENE_ID,
});

const buildStackFromPath = (): SceneStackEntry[] => {
  if (typeof window === "undefined") return [rootStackEntry()];
  const segments = window.location.pathname
    .replace(/\/+$/, "")
    .replace(/^\//, "")
    .split("/")
    .filter(Boolean);
  if (segments.length === 0) return [rootStackEntry()];

  const root = segments[0];
  if (!KNOWN_SCENES.has(root)) return [rootStackEntry()];

  const stack: SceneStackEntry[] = [
    { sceneId: root, poiFilterId: SCENE_POI_ID[root] ?? root },
  ];

  // Inside-сцены и сцены-эпохи живут во втором сегменте
  // (/kaaba/kaaba-inside, /kaaba/kaaba-1890s). Принимаем только если в
  // соответствующем реестре есть запись с матчащимся родителем — иначе
  // мусорный путь молча падает на родительскую сцену.
  const child = segments[1];
  if (child && isInsideScene(child) && INSIDE_SCENE_PARENT[child] === root) {
    stack.push({ sceneId: child, poiFilterId: child });
  } else if (child && isEraScene(child) && ERA_SCENE_PARENT[child] === root) {
    // Тот же SCENE_POI_ID-lookup, что в navigateToHistoricalScene (сейчас
    // для эр это fallback `?? child` — их id совпадает с backend id).
    stack.push({ sceneId: child, poiFilterId: SCENE_POI_ID[child] ?? child });
  }

  return stack;
};

const POI_ARTIFACT_PRELOADER_MIN_DURATION = 2000;

/**
 * Minimum visible window for the particle-sphere preloader when returning
 * to a main scene from an inside-scene (snapshot-restore path). On a warm
 * HTTP cache the splat parse finishes in <100 ms — without this floor the
 * preloader would be skipped entirely by the delayed-show 150 ms gate
 * inside `ParticleSpherePreloader`, and the user would see the previous
 * scene "snap back" instantly. The 300 ms window gives a clean visual
 * handoff (sphere fade → snapshot pose) without dragging out the wait.
 */
const SCENE_RESTORE_MIN_PRELOADER_MS = 300;

/**
 * Delay (ms) between old canvas unmount and new canvas mount on scene
 * transitions (splat → map, map → splat, splat → splat).
 *
 * iOS Safari releases GPU memory from `renderer.forceContextLoss()` ASYNCHRONOUSLY
 * (lazy memory-pressure handler, 100s of ms). Without this gap, the new
 * WebGLRenderer allocation races with the pending release and hits OOM, which
 * triggers `webglcontextlost` and tab reload. The delay gives iOS a paint frame
 * + idle time to actually reclaim the old context's GPU memory.
 *
 * NOTE: the delay only works if the gate closes SYNCHRONOUSLY during render
 * (see gateState below). If the gate closes in useEffect, React mounts the
 * new canvas in the same tick as old canvas dispose and the delay never gates
 * the initial overlap — the tab still OOMs. This is the bug that required the
 * setState-during-render pattern.
 */
const SCENE_TRANSITION_GPU_RECLAIM_DELAY_MS = 0;

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  // Bumped on every clock-button click to replay the one-shot hand spin
  // (changing the icon's `key` remounts it → the `.clockSpin` animation re-runs,
  // even on rapid clicks). 0 = never clicked yet → no spin on first appearance.
  const [clockSpinNonce, setClockSpinNonce] = useState(0);
  const [glbApi, setGlbApiRaw] = useState<GlbSceneApi | null>(null);
  const glbApiSceneIdRef = useRef<string | null>(null);
  const [splatApi, setSplatApi] = useState<SplatSceneApi | null>(null);
  const [poiArtifactApi, setPoiArtifactApi] = useState<GlbSceneApi | null>(null);
  const [poiArtifactPreloaderHold, setPoiArtifactPreloaderHold] = useState(false);

  const {
    appState,
    sceneLoadingState,
    audioConfig,
    updateAudio,
    orderedSightIds,
    sights,
    activeScene,
    activeSceneId,
    poiArtifactId,
    isPoiArtifactOpen,
    sceneStack,
    configFetched,
    historyMode,
  } = useUnit({
    appState: $appState,
    sceneLoadingState: $sceneLoadingState,
    audioConfig: $audioConfig,
    updateAudio: updateAudioConfig,
    orderedSightIds: $sceneOrderedSightIds,
    sights: $sceneSights,
    activeScene: $activeScene,
    activeSceneId: $activeSceneId,
    poiArtifactId: $poiArtifactId,
    isPoiArtifactOpen: $isPoiArtifactOpen,
    sceneStack: $sceneStack,
    configFetched: $configFetched,
    historyMode: $historyMode,
  });

  const i18n = useI18n();
  const { theme, setTheme } = useTheme();
  const layoutMode = useLayoutMode();
  useKeyboardNavigation();
  const orderedSights = useMemo(
    () =>
      orderedSightIds
        .map((id) => sights[id])
        .filter((item): item is Sight => Boolean(item)),
    [orderedSightIds, sights]
  );

  const showDebugControls = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.has("dev");
  }, []);

  const showCameraDebug = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.has("camera-debug");
  }, []);

  const showFlyoverTuner = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.has("flight");
  }, []);

  const showCrowTuner = useMemo(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.has("crow");
  }, []);


  // Scene type detection (main scene — not affected by PoiArtifact overlay)
  const isSplat = activeSceneId != null && isSplatScene(activeSceneId);
  const splatConfig = activeSceneId != null ? getSplatSceneConfig(activeSceneId) : undefined;
  const splatHudDisabled = isSplat && splatConfig?.disableHud === true;

  const isTopLevelGlb = activeSceneId != null && !isSplat && isGlbScene(activeSceneId);

  // PoiArtifact entry (from overlay store, not from activeSceneId)
  const poiArtifactEntry = poiArtifactId != null
    ? getPoiArtifactConfig(poiArtifactId)
    : undefined;

  const setGlbApi = useCallback((api: GlbSceneApi | null) => {
    glbApiSceneIdRef.current = api != null ? activeSceneId : null;
    setGlbApiRaw(api);
  }, [activeSceneId]);

  // Treat glbApi as stale if scene changed but old API hasn't been cleaned up yet
  const glbApiCurrent = glbApi != null && glbApiSceneIdRef.current === activeSceneId;

  // --- PoiArtifact preloader ---
  // Если preview уже загрузил модель (host cached для этого sceneId) — прелоадер
  // не показываем вовсе: canvas переезжает из preview-карточки в full-screen
  // мгновенно через `api.resume(newContainer)`, без моргания прелоадера.
  const poiArtifactPreloaderVisible =
    isPoiArtifactOpen
    && !(poiArtifactId && isPoiArtifactCached(poiArtifactId))
    && (!poiArtifactApi || poiArtifactPreloaderHold);

  // Minimum display time for PoiArtifact preloader (entering)
  useEffect(() => {
    logMemoryEvent('App poiArtifact effect', {
      isPoiArtifactOpen,
      poiArtifactId,
      isCached: poiArtifactId ? isPoiArtifactCached(poiArtifactId) : null,
      hasApi: !!poiArtifactApi,
    });
    if (!isPoiArtifactOpen) {
      setPoiArtifactPreloaderHold(false);
      setPoiArtifactApi(null);
      recordPoiArtifactClose();
      return;
    }
    recordPoiArtifactOpen();
    // При повторном открытии того же артефакта content уже в памяти —
    // прелоадер не нужен, сразу показываем сцену.
    if (poiArtifactId && isPoiArtifactCached(poiArtifactId)) {
      setPoiArtifactPreloaderHold(false);
      return;
    }
    setPoiArtifactPreloaderHold(true);
    const timer = setTimeout(() => setPoiArtifactPreloaderHold(false), POI_ARTIFACT_PRELOADER_MIN_DURATION);
    return () => clearTimeout(timer);
  }, [isPoiArtifactOpen, poiArtifactId]);

  // --- Pause/resume main scene when PoiArtifact is open ---
  useEffect(() => {
    if (!splatApi) return;
    if (isPoiArtifactOpen) {
      logMemoryEvent('App: splat.pause() on artifact open');
      splatApi.pause();
    } else {
      logMemoryEvent('App: splat.resume() on artifact close');
      splatApi.resume();
    }
  }, [isPoiArtifactOpen, splatApi]);

  // --- Scene transition GPU reclaim gate (iOS OOM fix) ---
  // Когда activeSceneId меняется, ОДИН canvas дизmosится, а другой должен
  // смонтироваться. iOS Safari освобождает GPU от forceContextLoss()
  // асинхронно (~100-500 ms); если новый canvas создаёт WebGLRenderer
  // в том же tick'е — iOS OOM-kill'ит вкладку.
  //
  // КРИТИЧНО: гейт должен закрываться СИНХРОННО во время render'а, а не
  // в useEffect. React запускает эффекты ДОЧЕРНИХ компонентов (SceneCanvas /
  // GlbSceneCanvas → создание WebGLRenderer) РАНЬШЕ эффекта родителя. К моменту когда
  // useEffect родителя сработает, новый renderer уже создан — overlap
  // контекстов уже произошёл. Используем паттерн "setState во время
  // рендера" — React корректно обрабатывает такой вызов, отменяя текущий
  // render и запуская новый с обновлённым state'ом. Дочерний mount
  // происходит только во втором render'е, когда гейт уже закрыт.
  const [gateState, setGateState] = useState<'open' | 'closing'>('open');
  const prevActiveSceneIdForGateRef = useRef<string | null | undefined>(undefined);

  // Synchronously detect transition during render and close the gate BEFORE
  // React commits new children.
  if (prevActiveSceneIdForGateRef.current !== activeSceneId) {
    const wasInitial = prevActiveSceneIdForGateRef.current === undefined;
    prevActiveSceneIdForGateRef.current = activeSceneId;
    if (!wasInitial && gateState === 'open') {
      setGateState('closing');
    }
  }

  const canMountSceneCanvas = gateState === 'open';

  useEffect(() => {
    if (gateState !== 'closing') return;
    logMemoryEvent('App scene transition gate: closed, waiting', {
      activeSceneId,
      delayMs: SCENE_TRANSITION_GPU_RECLAIM_DELAY_MS,
    });
    const timer = setTimeout(() => {
      logMemoryEvent('App scene transition gate: opening', { activeSceneId });
      setGateState('open');
    }, SCENE_TRANSITION_GPU_RECLAIM_DELAY_MS);
    return () => clearTimeout(timer);
    // activeSceneId included only for logging correctness; the dep that
    // actually drives the effect is gateState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateState]);

  // DEBUG: логируем каждую смену activeSceneId — чтобы на iPhone видеть точный
  // момент transition (kaaba → null → new scene) в корреляции с mount/dispose
  // событиями компонентов.
  const debugActiveSceneIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const prev = debugActiveSceneIdRef.current;
    debugActiveSceneIdRef.current = activeSceneId;
    logMemoryEvent('App activeSceneId change', {
      prev: prev === undefined ? '(initial)' : prev,
      next: activeSceneId,
      hasSplatApi: !!splatApi,
      hasArtifactApi: !!poiArtifactApi,
      isPoiArtifactOpen,
      hasArtifactCache: poiArtifactId ? isPoiArtifactCached(poiArtifactId) : false,
    });
  }, [activeSceneId, splatApi, poiArtifactApi, isPoiArtifactOpen, poiArtifactId]);

  // Unified GLB — covers top-level GLB scenes only (PoiArtifact overlay renders separately)
  const isGlb = isTopLevelGlb;
  const glbConfig: GlbSceneConfig | undefined = isTopLevelGlb
    ? getGlbSceneConfig(activeSceneId!)
    : undefined;

  const previewIsReady = isSplat && sceneLoadingState.status === "preview";
  const sceneIsReady = isSplat && sceneLoadingState.status === "ready";

  // GLB scenes with preview model
  const glbHasPreview = isGlb && glbConfig?.preview != null;
  const glbPreviewReady = glbHasPreview && sceneLoadingState.status === "preview";
  const glbSceneReady = glbHasPreview && sceneLoadingState.status === "ready";

  // CameraViewOffsetProvider: ready for GLB scenes
  const glbViewOffsetReady = isGlb && (
    glbHasPreview ? (glbPreviewReady || glbSceneReady) : !!glbApi
  );

  // Scene-restore window: held true while we want the particle-sphere
  // preloader to remain visible on a snapshot-restore mount, suppressing
  // SceneTitle / SceneOnboarding / progress bar / HUD until the sphere
  // gets its mandated 300 ms display. Snapshot presence is read once at
  // mount via `getState()` — subscribing would re-trigger the window if
  // a fresh save happens during the same scene lifetime (it shouldn't,
  // but we want a strict edge-once trigger).
  // Scene-state restore window. `restoreActive=true` означает:
  // ParticleSpherePreloader полностью overlay'ит canvas, скрывая
  // preview-модель и fade-in сцены. Активация ставится в useLayoutEffect
  // (sync до paint) при mount сплат-сцены, для которой есть snapshot.
  //
  // Деактивация — двухусловная (фиксит баг "вижу часть preloader-анимации
  // основной сцены"): сфера должна оставаться поверх до тех пор, пока
  // (a) сплат реально загружен (`sceneIsReady`), И (b) прошло минимум
  // `SCENE_RESTORE_MIN_PRELOADER_MS` мс. Простой setTimeout без проверки
  // `sceneIsReady` снимал сферу через 300мс независимо от того, готова
  // сцена или нет — на medium-network загрузке `.sog` пользователь видел
  // preview-модель и splat reveal под прозрачным после-таймера слоем.
  //
  // Snapshot key — `activeScene.poiFilterId`, не `splatConfig.id`. Это
  // разные значения для main-сцен (kaaba: poiFilterId='main', config.id
  // ='kaaba') — раньше промахивались и restoreActive не активировался.
  const [restoreActive, setRestoreActive] = useState(false);
  const restoreStartRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (!isSplat || !activeScene) {
      setRestoreActive(false);
      restoreStartRef.current = null;
      return;
    }
    const hasSnapshot = $sceneStateSnapshots.getState().has(activeScene.poiFilterId);
    if (!hasSnapshot) {
      setRestoreActive(false);
      restoreStartRef.current = null;
      return;
    }
    setRestoreActive(true);
    restoreStartRef.current = performance.now();
  }, [isSplat, activeScene?.poiFilterId]);

  // Tear down restoreActive when scene is ready AND minimum duration
  // elapsed. Re-runs whenever sceneIsReady flips — on warm cache scene
  // may be ready before we set restoreActive (race-proof: restoreStartRef
  // is non-null only if restoreActive was set to true above).
  useEffect(() => {
    if (!restoreActive) return;
    if (!sceneIsReady) return;
    const startedAt = restoreStartRef.current;
    if (startedAt == null) {
      setRestoreActive(false);
      return;
    }
    const elapsed = performance.now() - startedAt;
    const remaining = Math.max(0, SCENE_RESTORE_MIN_PRELOADER_MS - elapsed);
    if (remaining === 0) {
      setRestoreActive(false);
      restoreStartRef.current = null;
      return;
    }
    const timer = setTimeout(() => {
      setRestoreActive(false);
      restoreStartRef.current = null;
    }, remaining);
    return () => clearTimeout(timer);
  }, [restoreActive, sceneIsReady]);

  const [preloaderDone, setPreloaderDone] = useState(false);
  useEffect(() => {
    if (sceneLoadingState.splatPhase === 'ready' && !restoreActive) {
      const timer = setTimeout(() => setPreloaderDone(true), 400);
      return () => clearTimeout(timer);
    }
    setPreloaderDone(false);
  }, [sceneLoadingState.splatPhase, restoreActive]);

  const [hudReady, setHudReady] = useState(false);
  useEffect(() => {
    if (sceneIsReady && !restoreActive) {
      const timer = setTimeout(() => setHudReady(true), HUD_REVEAL_DELAY);
      return () => clearTimeout(timer);
    }
    setHudReady(false);
  }, [sceneIsReady, restoreActive]);

  // GLB with preview: delayed states for SceneExpandedEffect / HUD
  const [glbPreloaderDone, setGlbPreloaderDone] = useState(false);
  useEffect(() => {
    if (glbSceneReady) {
      const timer = setTimeout(() => setGlbPreloaderDone(true), 400);
      return () => clearTimeout(timer);
    }
    setGlbPreloaderDone(false);
  }, [glbSceneReady]);

  const [glbHudReady, setGlbHudReady] = useState(false);
  useEffect(() => {
    if (glbSceneReady) {
      const timer = setTimeout(() => setGlbHudReady(true), HUD_REVEAL_DELAY);
      return () => clearTimeout(timer);
    }
    setGlbHudReady(false);
  }, [glbSceneReady]);

  // Unified ready flags (scene-type agnostic)
  const anyPreviewReady = previewIsReady || glbPreviewReady;
  const anySceneReady = sceneIsReady || glbSceneReady;
  const anyPreloaderDone = preloaderDone || glbPreloaderDone;
  const anyHudReady = hudReady || glbHudReady || (!glbHasPreview && isGlb && glbApiCurrent);

  // HUD unmounts on scene-to-scene transition (root↔root).
  // Chat messages persist in $assistantMessages — only reset on return to map.
  const hudMountedRef = useRef(false);
  const [hudMounted, setHudMounted] = useState(false);
  const animateHudRef = useRef(true);
  const prevActiveSceneIdRef = useRef(activeSceneId);

  useEffect(() => {
    // Scenes with `disableHud` (overall) never mount AssistantHud — keep
    // hudMounted=false so the menu button + MenuOverlay never appear.
    if (anyHudReady && !hudMountedRef.current && !splatHudDisabled) {
      hudMountedRef.current = true;
      setHudMounted(true);
    }
  }, [anyHudReady, splatHudDisabled]);

  useEffect(() => {
    if (activeSceneId == null) {
      hudMountedRef.current = false;
      setHudMounted(false);
      animateHudRef.current = true;
    }
  }, [activeSceneId]);

  // Scene-to-scene transition (root→different root).
  // Unmount HUD so the new scene's loading pipeline plays fully.
  useEffect(() => {
    const prev = prevActiveSceneIdRef.current;
    prevActiveSceneIdRef.current = activeSceneId;

    if (prev != null && activeSceneId != null && prev !== activeSceneId) {
      assistantSkipAllAnimations();
      hudMountedRef.current = false;
      setHudMounted(false);
      animateHudRef.current = true;
    }
  }, [activeSceneId]);


  // Initialize stack from URL on mount
  useEffect(() => {
    const initial = buildStackFromPath();
    if (initial.length > 0) {
      rebuildStack(initial);
    }
     
  }, []);

  // Auto-enter root scene whenever the stack becomes empty (e.g. after
  // resetNavigation on "return to map"). Initial mount is skipped via ref —
  // otherwise this effect races the `rebuildStack` effect above: both run on
  // first commit with closure-captured `sceneStack === []` (Effector default),
  // and this one would append a duplicate `overall` after rebuildStack already
  // populated the store, ending up with `[{overall}, {overall}]`.
  const autoEnterMountRef = useRef(true);
  useEffect(() => {
    if (autoEnterMountRef.current) {
      autoEnterMountRef.current = false;
      return;
    }
    if (sceneStack.length !== 0) return;
    enterScene(rootStackEntry());
  }, [sceneStack.length]);

  // For scenes with `disableHud` (e.g. overall): canvas is full-screen from
  // the first frame. AssistantChatPanel never mounts to set
  // `--panel-current-height` / `--canvas-panel-gap`, so we zero them
  // ourselves on `#app`. Cleanup on scene change restores defaults so the
  // next scene's chat panel can take over.
  useEffect(() => {
    if (!splatHudDisabled) return;
    const root = document.getElementById('app');
    if (!root) return;
    root.style.setProperty('--panel-current-height', '0px');
    root.style.setProperty('--canvas-panel-gap', '0px');
    return () => {
      root.style.removeProperty('--panel-current-height');
      root.style.removeProperty('--canvas-panel-gap');
    };
  }, [splatHudDisabled]);

  // Desktop-mode layout marker on `#app` (CSS-vars override is in
  // spacing.css `#app.desktopLayout`). Scoped class — not a `:root`
  // media query — so the `MobilePreviewFrame.screen` ancestor of the
  // canvas does NOT shadow the override and `SceneExpandedEffect`'s
  // inline writes on `#app` can still reach the canvas in preview
  // mode. In preview `useLayoutMode` is force-mobile, the class is
  // absent, and the canvas falls back to its mobile geometry.
  useLayoutEffect(() => {
    const root = document.getElementById('app');
    if (!root) return;
    root.classList.toggle('desktopLayout', layoutMode === 'desktop');
    return () => {
      root.classList.remove('desktopLayout');
    };
  }, [layoutMode]);

  // Analytics: track cross-breakpoint resize and keep user properties in
  // sync. `identifyDevice` already sets layout_mode / viewport_width /
  // is_preview_frame at session start (via `SceneCanvas` mount); this
  // effect handles runtime flips so segmentation stays accurate.
  const prevLayoutModeRef = useRef<typeof layoutMode | null>(null);
  useEffect(() => {
    const prev = prevLayoutModeRef.current;
    prevLayoutModeRef.current = layoutMode;
    if (prev === null || prev === layoutMode) return;
    layoutModeChanged(prev, layoutMode);
    identifyLayoutMode();
  }, [layoutMode]);

  // Enter main scene from global map
  // const handleEnterScene = useCallback((sceneId: string) => {
  //   resetNavigation();
  //   enterScene({ sceneId, poiFilterId: SCENE_POI_ID[sceneId] ?? sceneId });
  //   setMenuOpen(false);
  //   sceneEntered(sceneId);
  // }, []);

  // Close PoiArtifact overlay
  const handleClosePoiArtifact = useCallback(() => {
    closePoiArtifact();
  }, []);

  const handleSoundToggle = useCallback(() => {
    updateAudio({ soundOn: !audioConfig.soundOn });
  }, [audioConfig.soundOn, updateAudio]);

  const handleReturnToMap = useCallback(() => {
    logMemoryEvent('App handleReturnToMap', {
      fromSceneId: activeSceneId,
      hasArtifactCache: poiArtifactId ? isPoiArtifactCached(poiArtifactId) : false,
    });
    sceneExited(activeSceneId);
    resetNavigation();
    setMenuOpen(false);
  }, [activeSceneId, poiArtifactId]);

  // Open the redesigned menu overlay + kick off the per-scene tour
  // prefetch (fills progress counters for not-yet-visited scenes).
  const handleOpenMenu = useCallback(() => {
    setMenuOpen(true);
    menuOpened();
  }, []);

  // Toggle the historical-scenes feature (clock button). Routing (closed →
  // slider, open → close) lives in the store (`clockButtonTapped`); analytics
  // (Historical Feature Opened / Closed) derive from the `$historyMode`
  // transition in historicalScenesTracker — a UI dispatch here would double-fire
  // on rapid double-clicks (the `useUnit` snapshot lags the store).
  const handleToggleHistory = useCallback(() => {
    setClockSpinNonce((n) => n + 1); // replay the hand spin on every click
    clockButtonTapped();
  }, []);

  const handleCloseHistory = useCallback(() => {
    closeHistory();
  }, []);

  // Menu → switch to a main scene (reset stack + enter → full reload).
  // `menuSceneSelected` analytics fires in MenuOverlay at the click site
  // (it knows the active tab + card kind); handlers only close + navigate.
  const handleEnterMainSceneFromMenu = useCallback(
    (urlId: string) => {
      menuClosed({ outcome: "scene_selected" });
      setMenuOpen(false);
      if (activeSceneId === urlId) return;
      sceneExited(activeSceneId);
      resetNavigation();
      enterScene({ sceneId: urlId, poiFilterId: SCENE_POI_ID[urlId] ?? urlId });
      sceneEntered(urlId);
    },
    [activeSceneId],
  );

  // Menu → enter an inside-scene (whole "Inside the Kaaba" row is clickable).
  // From the parent scene it's the same flow as the chat banner (snapshot +
  // push, so goBack restores the parent chat + pose). From anywhere else the
  // stack is rebuilt to `[parent, inside]` — URL `/kaaba/kaaba-inside` and the
  // "Back to {parent}" pill stay consistent.
  const handleEnterInsideSceneFromMenu = useCallback(
    (insideId: string) => {
      menuClosed({ outcome: "scene_selected" });
      setMenuOpen(false);
      // Alias-safe "already there" guard: `kaaba-inside` и `kaaba-inside-scene`
      // указывают на один SceneConfig.
      if (
        activeSceneId != null &&
        isInsideScene(activeSceneId) &&
        getInsideSceneConfig(activeSceneId) === getInsideSceneConfig(insideId)
      ) {
        return;
      }
      const parentId = INSIDE_SCENE_PARENT[insideId];
      if (!parentId) return;
      if (activeSceneId === parentId) {
        enterInsideScene(insideId);
        return;
      }
      sceneExited(activeSceneId);
      resetNavigation();
      // `rebuildStack` не стреляет `stopVirtualTour` (в отличие от enterScene),
      // а `resetNavigation` тур тоже не трогает — останавливаем явно.
      stopVirtualTour();
      rebuildStack([
        { sceneId: parentId, poiFilterId: SCENE_POI_ID[parentId] ?? parentId },
        { sceneId: insideId, poiFilterId: insideId },
      ]);
      sceneEntered(insideId);
    },
    [activeSceneId],
  );

  // Menu → enter a historical era scene. Все ветки (внутри семьи и с чужой
  // сцены) живут в navigateToHistoricalScene — там же гварды на stub-эры и
  // на текущую сцену. Эры сознательно НЕ идут в generic Scene Entered funnel
  // (docs/architecture/scene-registry.md).
  const handleEnterHistoricalSceneFromMenu = useCallback((eraId: string) => {
    menuClosed({ outcome: "scene_selected" });
    setMenuOpen(false);
    navigateToHistoricalScene(eraId);
  }, []);


  // Exit an inside-scene back to its parent (e.g. kaaba-inside → kaaba).
  // `goBack` pops the transient entry; activeSceneId reverts to the parent
  // and SceneCanvas remounts with the parent config (full reload + preloader).
  const handleBackFromInsideScene = useCallback(() => {
    if (activeSceneId && isInsideScene(activeSceneId)) {
      insideSceneBackPressed({
        sceneId: activeSceneId,
        parentSceneId: INSIDE_SCENE_PARENT[activeSceneId] ?? null,
      });
    }
    sceneExited(activeSceneId);
    goBack();
  }, [activeSceneId]);

  // Show back button only inside an inside-scene that has a parent
  // (currently: every entry in insideScenes registry has a parent).
  const isInsideSceneActive = activeSceneId != null && isInsideScene(activeSceneId);

  // Clock button — only on scenes that declare historical scenes (kaaba + its
  // era children). Data-driven via `sceneHasHistory`.
  const showHistoryButton =
    hudMounted && !isPoiArtifactOpen && sceneHasHistory(activeSceneId);

  // URL popstate — rebuild stack from URL (PoiArtifact overlay state is NOT in URL)
  const isPopstateRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handlePopState = () => {
      isPopstateRef.current = true;
      rebuildStack(buildStackFromPath());
      // setMenuOpen(false);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Sync browser URL to sceneStack (PoiArtifact overlay state is internal, not in URL).
  // Root scene (`overall`) is canonical at `/` — never `/overall`. Initial mount
  // with a non-canonical URL (e.g. `/overall` typed manually, `/unknown`
  // falling back to root) is rewritten via `replaceState` so the bogus entry
  // never enters browser history.
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const isJustRoot =
      sceneStack.length === 1 && sceneStack[0].sceneId === ROOT_SCENE_ID;
    const targetPath =
      sceneStack.length === 0 || isJustRoot
        ? "/"
        : `/${sceneStack.map((e) => e.sceneId).join("/")}`;
    const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
    if (currentPath === targetPath) {
      if (isInitialMountRef.current) isInitialMountRef.current = false;
      return;
    }
    if (isPopstateRef.current) {
      isPopstateRef.current = false;
      return;
    }
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      window.history.replaceState(null, "", `${targetPath}${window.location.search}`);
    } else {
      window.history.pushState(null, "", `${targetPath}${window.location.search}`);
    }
  }, [sceneStack]);

  return (
    <CameraViewOffsetProvider
      enabled={previewIsReady || sceneIsReady || glbViewOffsetReady}
      sceneKey={activeSceneId}
    >
      <div className={styles.app}>
        {hudMounted &&
          !isPoiArtifactOpen &&
          activeSceneId !== ROOT_SCENE_ID && (
            // Menu slot (@16). Swaps to a close (×) in SLIDER mode (matches
            // the design: menu → × when the era carousel shows).
            <Button
              appearance="ghost"
              className={styles.menuButton}
              aria-label={
                historyMode === "slider"
                  ? i18n.historicalScenes.close
                  : i18n.suggestions.openMenu
              }
              onClick={historyMode === "slider" ? handleCloseHistory : handleOpenMenu}
            >
              {historyMode === "slider" ? <CloseIcon /> : <ListIcon />}
            </Button>
          )}
        {/* Clock button (@+16+36+8, slot left of the menu). Toggles the
            historical feature. Slider mode → drops below the overlay
            (dimmed by the scrim). */}
        {showHistoryButton && (
          <Button
            appearance="ghost"
            className={cx(
              styles.historyButton,
              historyMode === "slider" && styles.historyButtonSlider,
            )}
            aria-label={i18n.historicalScenes.buttonLabel}
            aria-pressed={historyMode !== "closed"}
            onClick={handleToggleHistory}
          >
            {/* `key` remount replays the one-shot spin on each click; no class
                on first mount (nonce 0) so it stays still until interacted. */}
            <ClockIcon
              key={clockSpinNonce}
              className={clockSpinNonce > 0 ? styles.clockSpin : undefined}
            />
          </Button>
        )}
        {/* Back to parent — visible only inside an inside-scene (e.g.
            kaaba-inside → kaaba). PoiArtifact uses its own top-right
            close button at the same z-layer; the two never overlap.
            Gated by `hudMounted` так же, как и menu-кнопка выше — чтобы
            обе кнопки появлялись синхронно после прелоадера, а не
            торчали поверх ParticleSphere во время загрузки. */}
        {hudMounted && isInsideSceneActive && !isPoiArtifactOpen && (
          <Button
            appearance="ghost"
            className={styles.backButton}
            onClick={handleBackFromInsideScene}
          >
            <CaretLeftIcon />
            <span>
              {formatMessage(i18n.app.backTo, {
                name:
                  i18n.globalMap.markerLabels[
                    INSIDE_SCENE_PARENT[activeSceneId ?? ""] ?? ""
                  ] ?? "",
              })}
            </span>
          </Button>
        )}
        {isPoiArtifactOpen && (
          <Button
            appearance="ghost"
            className={styles.closePoiArtifactButton}
            onClick={handleClosePoiArtifact}
          >
            <CloseIcon />
          </Button>
        )}
        {activeSceneId == null ? (
          // Brief intermediate state between resetNavigation() and the
          // auto-enter effect that pushes ROOT_SCENE_ID back onto the stack.
          // Lasts a single React tick — nothing to render.
          <div className={styles.menuLayer} />
        ) : (
          <div className={styles.sceneLayer}>
            {/* Main splat scene — stays mounted even when PoiArtifact is open */}
            {isSplat && splatConfig && (
              <div
                className={cx(
                  styles.sceneContent,
                  (previewIsReady || sceneIsReady) && styles.sceneContentReady,
                )}
              >
                {/* SceneCanvas mount gated for same reason as the renderer-gate above.
                    Also gated by `configFetched` — without it, the canvas
                    mounts with sights=[] and then `setSights(...)` from the
                    `/api/config` response triggers a new orderedSights
                    reference, which re-runs SceneCanvas useEffect deps,
                    disposes the still-loading scene, and crashes async
                    loaders with `scene.add(null)` (createScene.ts:1101 +
                    PreviewModel.ts:265). Waiting for the config response
                    (success or failure) ensures sights are stable when
                    the scene mounts. Downstream (PoiMarkerManager /
                    PoiClusterEngine / assistantHud) tolerates `sights=[]`,
                    so a zero-POI response from the backend renders fine. */}
                {canMountSceneCanvas && configFetched && (
                  <SceneCanvas
                    sights={orderedSights}
                    config={splatConfig}
                    onApiReady={setSplatApi}
                  />
                )}
                <div className={styles.sceneBlurOverlay} />
                {showDebugControls && !isPoiArtifactOpen && (
                  <div dir="ltr">
                    <Leva collapsed titleBar={{ title: `Splat: ${activeSceneId}` }} />
                    <AppConfigPanel />
                    <SplatTransformPanel />
                    <SightCameraConfigPanel />
                  </div>
                )}
              </div>
            )}

            {/* Top-level GLB scene (e.g. mina) */}
            {isGlb && glbConfig && (
              <>
                {/* GlbSceneCanvas mount gated for GPU reclaim + configFetched
                    (same race as SceneCanvas — sights reference change after
                    /api/config response triggers re-mount and crashes async
                    loaders mid-flight). */}
                {canMountSceneCanvas && configFetched && (
                  <GlbSceneCanvas
                    config={glbConfig}
                    sights={orderedSights}
                    onApiReady={setGlbApi}
                  />
                )}
                <div className={styles.sceneBlurOverlay} />
                {!glbHasPreview && (
                  <SplashScreen visible={!glbApi} message={i18n.app.loading} />
                )}
                {showDebugControls && glbApi && !isPoiArtifactOpen && (
                  <div dir="ltr">
                    <Leva collapsed titleBar={{ title: `GLB: ${activeSceneId}` }} />
                    <GlbDevPanel api={glbApi} config={glbConfig} />
                  </div>
                )}
              </>
            )}

            {/* PoiArtifact overlay — renders on top of main scene */}
            {isPoiArtifactOpen && poiArtifactEntry?.type === 'glb' && (
              <div className={styles.poiArtifactOverlay}>
                <GlbSceneCanvas
                  config={poiArtifactEntry.config}
                  sights={[]}
                  onApiReady={setPoiArtifactApi}
                  fullscreen
                  suppressLoadingEvents
                  poiArtifactClaimant="fullscreen"
                  enableZoom
                />
                <ParticleSpherePreloader visible={poiArtifactPreloaderVisible} />
                {poiArtifactApi && (
                  <SceneBackgroundSwitcher
                    api={poiArtifactApi}
                    sceneId={poiArtifactEntry.config.id}
                    defaultBg={poiArtifactEntry.config.bgTheme}
                    fullscreen
                  />
                )}
                {showDebugControls && poiArtifactApi && (
                  <div dir="ltr">
                    <Leva
                      collapsed
                      titleBar={{ title: `Artifact: ${poiArtifactEntry.config.id}` }}
                    />
                    <GlbDevPanel api={poiArtifactApi} config={poiArtifactEntry.config} />
                  </div>
                )}
              </div>
            )}

            {/* Shared overlays — scene-type agnostic */}
            {isSplat && (
              <>
                <ParticleSpherePreloader
                  visible={
                    sceneLoadingState.status !== "error"
                    && (
                      restoreActive
                      || (!previewIsReady && !sceneIsReady)
                    )
                  }
                  instant={restoreActive}
                />
                <SceneLoadError
                  visible={sceneLoadingState.status === "error"}
                  message={i18n.app.loadError}
                  hint={i18n.app.loadErrorHint}
                  reloadLabel={i18n.app.reload}
                />
              </>
            )}
            <SceneProgressBar
              visible={previewIsReady && sceneLoadingState.splatPhase !== 'idle' && !preloaderDone && !restoreActive}
              splatLoadedBytes={sceneLoadingState.splatLoadedBytes}
              splatTotalBytes={sceneLoadingState.splatTotalBytes}
              splatPhase={sceneLoadingState.splatPhase}
            />
            <SceneTitle
              visible={anyPreviewReady && !anySceneReady && !anyPreloaderDone && !restoreActive}
              sceneName={activeSceneId ? (i18n.globalMap.markerLabels[activeSceneId] ?? activeSceneId) : ''}
              hideSubtitle={splatHudDisabled || splatConfig?.disableSubtitle === true}
            />
            {/* Onboarding is mobile-only — desktop has the sidebar
                always visible from `hudReady`, so the bottom-curtain
                hint copy has no surface to attach to. */}
            <SceneOnboarding visible={layoutMode === "mobile" && !splatHudDisabled && anyPreviewReady && !anyPreloaderDone && !restoreActive} />
            <MenuOverlay
              open={hudMounted && menuOpen && !isPoiArtifactOpen}
              onClose={() => {
                menuClosed({ outcome: "dismiss" });
                setMenuOpen(false);
              }}
              onEnterMainScene={handleEnterMainSceneFromMenu}
              onEnterInsideScene={handleEnterInsideSceneFromMenu}
              onEnterHistoricalScene={handleEnterHistoricalSceneFromMenu}
              onMainMap={() => {
                menuClosed({ outcome: "main_map" });
                handleReturnToMap();
              }}
              showMainMap={activeSceneId !== ROOT_SCENE_ID}
            />
            {/* Historical scenes — clock opens the era-card slider. Modal (dims
                + blocks the scene); closes on any navigation (store reset).
                Gated by `showHistoryButton`. */}
            {showHistoryButton && historyMode === "slider" && activeSceneId != null && (
              <HistoricalScenesSlider activeSceneId={activeSceneId} />
            )}
            {showCameraDebug && <CameraDebugOverlay />}
            {/* Flyover authoring panel (?flight) — collapsible; waypoint
                capture / replay / copy-config, session-state draft. */}
            {showFlyoverTuner && <FlyoverTuner />}
            {/* Tuner — только для restricted (top-down) сцен.
                Компонент сам проверяет `controls.orbit.panMode === 'groundPlane'`
                на runtime и возвращает null, если активная сцена не restricted. */}
            {showDebugControls && <RestrictedCameraTuner />}
            {/* Nav-POI tuner — Leva sliders for x/y/z of each navigation POI
                + checkbox for the red debug dot at the 3D-anchor projection.
                Internal null-check via SceneBindings.navPoiTuner → no panel
                appears on scenes without navigationPois. */}
            {showDebugControls && <NavPoiTuner />}
            {/* Experimental animated bird prop (?crow) — pos / rot / scale /
                clip sliders. Null until the GLB loads. */}
            {showCrowTuner && <CrowTuner />}

            {/* SceneExpandedEffect — only on first scene load before HUD ever
                mounts. Scenes with `disableHud` skip this entirely: the
                layout-override useEffect zeroes the same CSS-vars from mount,
                so the canvas is already full-screen, no animation needed.
                CRITICAL: SceneExpandedEffect.cleanup does `root.style.cssText
                = ''` — that wipes EVERY inline-var on `#app`, including the
                ones our layout-override sets. Mounting it on overall would
                cause a 138px gap to reappear at the bottom after HUD-reveal
                delay. Hence the `!splatHudDisabled` gate. */}
            {anyPreloaderDone && !anyHudReady && !hudMounted && !splatHudDisabled && <SceneExpandedEffect />}

            {/* Chat panel vs root-scene overlay.
                — On `overall` (root): `OverallSceneOverlay` replaces chat —
                  no input, no message history, just a horizontal slider
                  with intro welcome card + scene-banner cards (Kaaba /
                  Arafat / Mina / Muzdalifah). Variant B layout: overlay
                  zeroes `--panel-current-height` / `--canvas-panel-gap`
                  so canvas hugs the bottom of the safe area while the
                  slider floats over it.
                — On any other scene: standard `AssistantHud` (chat with
                  input, suggestions, scroll, etc.).
                Both are skipped entirely when `splatHudDisabled === true`
                (currently no scene sets that flag, but the gate stays).
                Both are hidden via `display:none` when a PoiArtifact
                overlay is open — preserves their DOM/scroll state. */}
            {hudMounted && !splatHudDisabled && (
              activeSceneId === ROOT_SCENE_ID && splatConfig
                ? (
                  <OverallSceneOverlay
                    splatApi={splatApi}
                    splatConfig={splatConfig}
                    hidden={isPoiArtifactOpen}
                  />
                )
                : (
                  <AssistantHud
                    animateInitialOffset={animateHudRef.current}
                    hidden={isPoiArtifactOpen}
                  />
                )
            )}
          </div>
        )}
      </div>
    </CameraViewOffsetProvider>
  );
}

export default App;
